import { loadable } from '@change-corgi/core/react/loadable';

import type { AppRoute } from 'src/shared/routes';

import { createRoutes, handleRecruiter, Loading, petitionSlugCanonicalRedirect } from 'src/app/shared/routes';

import { resolvePetitionL10nForBotsFromSlugParam } from './shared/resolvePetitionL10nForBotsFromSlugParam';
import { shareUrlCanonicalRedirect } from './shared/shareUrlCanonicalRedirect';

export const ROUTES: readonly AppRoute[] = [
	/**
	 * Canonical petition route
	 */
	{
		id: 'petition-details',
		path: '/p/:slug',
		component: loadable(async () => import(/* webpackChunkName: "pages/petition_gamma" */ '../index'), {
			exportName: 'PetitionGammaDetails',
			fallback: Loading,
		}),
		frame: true,
		beforeServerRender: async (context) => {
			await handleRecruiter(context, context.params.slug);
			await petitionSlugCanonicalRedirect(context);
		},
		ssr: {
			enabled: true,
			seo: true,
			resolveL10n: resolvePetitionL10nForBotsFromSlugParam,
			/**
			 * IMPORTANT!
			 *
			 * This SSR config makes adds restrictions on how user data can be accessed within the page.
			 *
			 * Guest: The page is cached server-side. This means that any components rendering server-side MUST NOT
			 * request user-specific information. Our session utilities will throw an error to prevent this mistake. Any
			 * user data MUST be loaded & accessed through pageUserData only.
			 *
			 * Non-guest (logged-in or identified): The page is not cached, so the above is not a problem. But this
			 * means if you only test while logged in you may overlook an error in the guest experience.
			 *
			 * See doc/server-basics.md for more info.
			 */
			config: ({ loginState }) => {
				// Cache for geusts
				if (loginState === 'GUEST') {
					return {
						disallowUserData: true,
						cache: {
							enabled: true,
							lifetimeInSeconds: 1 * 60, // 1min
							queryParams: ['expired_session', 'best_share_visual'],
						},
					};
				}
				// Avoid caching for non-guest - mainly so starters & staff see updates immediately.
				return {};
			},
		},
	},
	/**
	 * Bot-share-routes
	 */
	...createRoutes(
		{
			frame: true,
			beforeServerRender: shareUrlCanonicalRedirect,
			ssr: {
				enabled: true,
				seo: true, // enabled so that twitter/FB can generate a preview
				resolveL10n: resolvePetitionL10nForBotsFromSlugParam,
				config: {
					disallowUserData: true,
				},
			},
		},
		[
			{
				// This route renders the same component as the canonical route. The main component
				// includes the right meta tags when a petition is a victory.
				id: 'petition-details-victory',
				path: '/p/:slug/share_victory',
				component: loadable(async () => import(/* webpackChunkName: "pages/petition_gamma" */ '../index'), {
					exportName: 'PetitionGammaDetails',
					fallback: Loading,
				}),
			},
			{
				id: 'petition-details-custom-preview-link-sfs',
				path: '/p/:slug/sfs/:shareChannel/:sharerUserId/:previewId',
				component: loadable(async () => import(/* webpackChunkName: "pages/petition_gamma" */ '../index'), {
					exportName: 'PetitionDetailsShareForStarters',
					fallback: Loading,
				}),
			},
			{
				id: 'petition-details-bandit-sfs',
				path: '/p/:slug/sfs/:shareChannel/:sharerUserId',
				component: loadable(async () => import(/* webpackChunkName: "pages/petition_gamma" */ '../index'), {
					exportName: 'PetitionDetailsShareForStarters',
					fallback: Loading,
				}),
			},
			{
				id: 'petition-details-bandit-nftexp',
				path: '/p/:slug/nftexp/:experimentName/:experimentVariantName/:sharerUserId',
				component: loadable(async () => import(/* webpackChunkName: "pages/petition_gamma" */ '../index'), {
					exportName: 'PetitionDetailsFacebookBandit',
					fallback: Loading,
				}),
			},
			{
				id: 'petition-details-experiment-fbogexp',
				path: '/p/:slug/fbogexp/:experimentName/:experimentVariantName',
				component: loadable(async () => import(/* webpackChunkName: "pages/petition_gamma" */ '../index'), {
					exportName: 'PetitionDetailsFacebookExperiment',
					fallback: Loading,
				}),
			},
			{
				id: 'petition-details-bandit-fbog',
				path: '/p/:slug/fbog/:sharerUserId',
				component: loadable(async () => import(/* webpackChunkName: "pages/petition_gamma" */ '../index'), {
					exportName: 'PetitionDetailsFacebook',
					fallback: Loading,
				}),
			},
			{
				id: 'petition-details-bandit-exp',
				path: '/p/:slug/exp/:shareType/:experimentName/:experimentVariantName/:sharerUserId',
				component: loadable(async () => import(/* webpackChunkName: "pages/petition_gamma" */ '../index'), {
					exportName: 'PetitionDetailsBandit',
					fallback: Loading,
				}),
			},
			{
				id: 'petition-details-bandit-exp-v2',
				path: '/p/:slug/exp/v2/:shareType/:experimentName/:experimentVariantName/:sharerUserId',
				component: loadable(async () => import(/* webpackChunkName: "pages/petition_gamma" */ '../index'), {
					exportName: 'PetitionDetailsBanditV2',
					fallback: Loading,
				}),
			},
		],
	),
];
